import { createLogger } from "../logger";
import { fetchFromCalWithRetry } from "./utils";

const logger = createLogger("lib/cal/admin");

/**
 * Error thrown when a Cal.com Admin API operation fails.
 * Avoids exposing sensitive data (tokens, emails) in error messages.
 */
export class CalAdminApiError extends Error {
  constructor(
    public readonly operation: string,
    public readonly errorCode?: string,
    public readonly errorMessage?: string,
  ) {
    const detailText = errorMessage || errorCode;
    const details = detailText ? `: ${detailText}` : "";
    super(`Cal.com API error during ${operation}${details}`);
    this.name = "CalAdminApiError";
  }
}

export type CalLocale =
  | "ar"
  | "ca"
  | "de"
  | "es"
  | "eu"
  | "he"
  | "id"
  | "ja"
  | "lv"
  | "pl"
  | "ro"
  | "sr"
  | "th"
  | "vi"
  | "az"
  | "cs"
  | "el"
  | "es-419"
  | "fi"
  | "hr"
  | "it"
  | "km"
  | "nl"
  | "pt"
  | "ru"
  | "sv"
  | "tr"
  | "zh-CN"
  | "bg"
  | "da"
  | "en"
  | "et"
  | "fr"
  | "hu"
  | "iw"
  | "ko"
  | "no"
  | "pt-BR"
  | "sk"
  | "ta"
  | "uk"
  | "zh-TW";

export interface CreateManagedUserRequest {
  email: string;
  name: string;
  timeFormat?: 12 | 24;
  weekStart?: "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";
  timeZone?: string;
  locale?: CalLocale;
  /** Cal.com stores this URL as-is; the Booker atom loads it in the browser. Must not expire. */
  avatarUrl?: string;
}

interface CreateManagedUserResponse {
  status: "success" | "error";
  data: CalManagedUser;
}

export interface CalManagedUser {
  user: {
    id: number;
    email: string;
    username: string;
    name: string;
    timeZone: string;
    weekStart: string;
    createdDate: string;
    timeFormat: number;
    defaultScheduleId: number | null;
    locale: string;
    avatarUrl?: string | null;
  };
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
}

interface RefreshAccessTokenRequest {
  calUserId: number;
  refreshToken: string;
}

interface RefreshAccessTokenResponse {
  status: "success" | "error";
  data: {
    accessToken: string;
    accessTokenExpiresAt: number;
    refreshToken: string;
  };
}

interface GetAllManagedUsersResponse {
  status: "success" | "error";
  data: CalManagedUser["user"][];
}

interface GetManagedUserResponse {
  status: "success" | "error";
  data: CalManagedUser["user"];
}

export interface UpdateManagedUserRequest {
  name?: string;
  email?: string;
  timeFormat?: 12 | 24;
  weekStart?: "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";
  timeZone?: string;
  locale?: CalLocale;
  /** Cal.com stores this URL as-is; the Booker atom loads it in the browser. Must not expire. */
  avatarUrl?: string;
}

interface UpdateManagedUserResponse {
  status: "success" | "error";
  data: CalManagedUser["user"];
}

interface RefreshManagedUserTokensResponse {
  status: "success" | "error";
  data: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: number;
  };
}

export interface CalWebhook {
  id: number;
  subscriberUrl: string;
  eventType: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GetAllWebhooksResponse {
  status: "success" | "error";
  data: CalWebhook[];
}

// v2 Webhook types
export interface CreateWebhookRequestV2 {
  subscriberUrl: string;
  triggers: string[];
  active: boolean;
  payloadTemplate?: string;
  secret?: string;
}

export interface CalWebhookV2 {
  id: number;
  subscriberUrl: string;
  active: boolean;
  triggers: string[];
  userId?: number;
  payloadTemplate?: string;
  secret?: string;
}

interface CreateWebhookResponseV2 {
  status: "success" | "error";
  data: CalWebhookV2;
}

interface DeleteWebhookResponseV2 {
  status: "success" | "error";
  data: CalWebhookV2;
}

export interface UpdateWebhookRequestV2 {
  subscriberUrl?: string;
  triggers?: string[];
  active?: boolean;
  payloadTemplate?: string;
  secret?: string;
}

interface UpdateWebhookResponseV2 {
  status: "success" | "error";
  data: CalWebhookV2;
}

export class CalAdminClient {
  constructor(
    private readonly clientId: string,
    private readonly clientSecret: string,
  ) {}

  /**
   * Unwraps a Cal.com API response, throwing an error if the status is not "success".
   * This ensures consistent error handling across all API calls.
   */
  private unwrapResponse<
    T extends {
      status: "success" | "error";
      data: unknown;
      error?: { code?: string; message?: string };
    },
  >(response: T, operation: string): T["data"] {
    if (response.status !== "success") {
      // Log full response for debugging but don't expose in error message
      logger.error(`Cal.com API error during ${operation}`, { response });
      throw new CalAdminApiError(operation, response.error?.code, response.error?.message);
    }
    return response.data as T["data"];
  }

  async createManagedUser(
    request: CreateManagedUserRequest,
  ): Promise<CreateManagedUserResponse["data"]> {
    const response = await this.post<CreateManagedUserResponse>(
      `/oauth-clients/${this.clientId}/users`,
      request,
    );
    return this.unwrapResponse(response, "createManagedUser");
  }

  async getAllManagedUsers(): Promise<GetAllManagedUsersResponse["data"]> {
    const response = await this.get<GetAllManagedUsersResponse>(
      `/oauth-clients/${this.clientId}/users`,
    );
    return this.unwrapResponse(response, "getAllManagedUsers");
  }

  async getManagedUser(userId: number): Promise<GetManagedUserResponse["data"]> {
    const response = await this.get<GetManagedUserResponse>(
      `/oauth-clients/${this.clientId}/users/${userId}`,
    );
    return this.unwrapResponse(response, "getManagedUser");
  }

  async getManagedUsersByEmails(emails: string[]): Promise<GetAllManagedUsersResponse["data"]> {
    // Cal stores managed-user emails lowercased and matches this filter case-sensitively, so a
    // mixed-case Practitioner email finds nothing and callers go on to create a duplicate (409).
    // toLowerCase, not toLocaleLowerCase - the result is a lookup key.
    const emailsParam = emails.map((email) => encodeURIComponent(email.toLowerCase())).join(",");
    const url = `/oauth-clients/${this.clientId}/users?emails=${emailsParam}`;
    const response = await this.get<GetAllManagedUsersResponse>(url);
    return this.unwrapResponse(response, "getManagedUsersByEmails");
  }

  async refreshManagedUserTokens(
    userId: number,
  ): Promise<RefreshManagedUserTokensResponse["data"]> {
    const response = await this.post<RefreshManagedUserTokensResponse>(
      `/oauth-clients/${this.clientId}/users/${userId}/force-refresh`,
    );
    return this.unwrapResponse(response, "refreshManagedUserTokens");
  }

  async findManagedUserByEmail(email: string): Promise<CalManagedUser["user"] | null> {
    const users = await this.getManagedUsersByEmails([email]);

    if (users.length > 0) {
      logger.info("Found existing Cal.com user", { calUserId: users[0].id });
      return users[0];
    }

    return null;
  }

  async removeManagedUser(userId: number): Promise<CreateManagedUserResponse["data"]> {
    const response = await this.delete<CreateManagedUserResponse>(
      `/oauth-clients/${this.clientId}/users/${userId}`,
    );
    return this.unwrapResponse(response, "removeManagedUser");
  }

  async updateManagedUser(
    userId: number,
    request: UpdateManagedUserRequest,
  ): Promise<UpdateManagedUserResponse["data"]> {
    const response = await this.patch<UpdateManagedUserResponse>(
      `/oauth-clients/${this.clientId}/users/${userId}`,
      request,
    );
    return this.unwrapResponse(response, "updateManagedUser");
  }

  async refreshAccessToken(
    request: RefreshAccessTokenRequest,
  ): Promise<RefreshAccessTokenResponse["data"]> {
    let response: RefreshAccessTokenResponse;
    try {
      response = await this.post<RefreshAccessTokenResponse>(`/oauth/${this.clientId}/refresh`, {
        refreshToken: request.refreshToken,
      });
    } catch (error) {
      logger.warn(`Error refreshing Cal access token, forcing refresh`, error);

      response = await this.post<RefreshAccessTokenResponse>(
        `/oauth-clients/${this.clientId}/users/${request.calUserId}/force-refresh`,
      );
    }
    return this.unwrapResponse(response, "refreshAccessToken");
  }

  /**
   * Fetches all platform webhooks using the Cal.com v2 API endpoint.
   */
  async getAllWebhooks(): Promise<GetAllWebhooksResponse["data"]> {
    const response = await this.get<GetAllWebhooksResponse>(
      `/v2/oauth-clients/${this.clientId}/webhooks`,
    );
    return this.unwrapResponse(response, "getAllWebhooks");
  }

  /**
   * Creates a Cal.com platform webhook (v2) with admin credentials.
   */
  async createWebhook(body: CreateWebhookRequestV2): Promise<CreateWebhookResponseV2["data"]> {
    const response = await this.post<CreateWebhookResponseV2>(
      `/v2/oauth-clients/${this.clientId}/webhooks`,
      body,
    );
    return this.unwrapResponse(response, "createWebhook");
  }

  /**
   * Deletes a Cal.com platform webhook (v2) by ID with admin credentials.
   */
  async deleteWebhook(webhookId: number | string): Promise<DeleteWebhookResponseV2["data"]> {
    const response = await this.delete<DeleteWebhookResponseV2>(
      `/v2/oauth-clients/${this.clientId}/webhooks/${webhookId}`,
    );
    return this.unwrapResponse(response, "deleteWebhook");
  }

  /**
   * Updates a Cal.com platform webhook (v2) by ID with admin credentials.
   */
  async updateWebhook(
    webhookId: number | string,
    body: UpdateWebhookRequestV2,
  ): Promise<UpdateWebhookResponseV2["data"]> {
    const response = await this.patch<UpdateWebhookResponseV2>(
      `/v2/oauth-clients/${this.clientId}/webhooks/${webhookId}`,
      body,
    );
    return this.unwrapResponse(response, "updateWebhook");
  }

  async get<T>(url: string): Promise<T> {
    return fetchFromCalWithRetry<T>(url, {
      headers: {
        "x-cal-client-id": this.clientId,
        "x-cal-secret-key": this.clientSecret,
      },
    });
  }

  async post<T>(url: string, body?: object): Promise<T> {
    return fetchFromCalWithRetry<T>(url, {
      method: "POST",
      headers: {
        "x-cal-client-id": this.clientId,
        "x-cal-secret-key": this.clientSecret,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(url: string): Promise<T> {
    return fetchFromCalWithRetry<T>(url, {
      method: "DELETE",
      headers: {
        "x-cal-client-id": this.clientId,
        "x-cal-secret-key": this.clientSecret,
      },
    });
  }

  async patch<T>(url: string, body?: object): Promise<T> {
    return fetchFromCalWithRetry<T>(url, {
      method: "PATCH",
      headers: {
        "x-cal-client-id": this.clientId,
        "x-cal-secret-key": this.clientSecret,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export function createCalAdminClient(): CalAdminClient {
  const clientId = process.env.NEXT_PUBLIC_CAL_CLIENT_ID;
  const clientSecret = process.env.CAL_CLIENT_SECRET;

  if (!clientId || !clientSecret) {
    throw new Error("Missing environment variables for Cal.com admin client");
  }

  return new CalAdminClient(clientId, clientSecret);
}
